import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { createFeed, uploadMultipleMediaFiles } from "../services/socialAuth";
import { extractGPSFromMediaFile } from "../utils/exifUtils";
import { testStorageConnection } from "../firebase/firebase";
import styles from "./CreateFeedPage.module.css";

interface MediaFile {
  file: File;
  type: "image" | "video";
  preview: string;
  optimized?: File;
  gpsInfo?: {
    latitude: number;
    longitude: number;
    address?: string;
  } | null;
}

const CreateFeedPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [content, setContent] = useState("");
  const [mediaFiles, setMediaFiles] = useState<MediaFile[]>([]);
  const [hashtags, setHashtags] = useState<string[]>([]);
  const [hashtagInput, setHashtagInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  // 뒤로가기
  const handleBack = () => {
    navigate(-1);
  };

  // 파일 선택
  const handleFileSelect = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = Array.from(event.target.files || []);

    if (mediaFiles.length + files.length > 10) {
      alert("최대 10개까지만 업로드할 수 있습니다.");
      return;
    }

    for (const file of files) {
      const type = file.type.startsWith("image/") ? "image" : "video";

      // 파일 크기 체크 (고품질 지원을 위해 제한 완화)
      const maxSize = type === "image" ? 25 * 1024 * 1024 : 100 * 1024 * 1024;
      if (file.size > maxSize) {
        alert(
          `${type === "image" ? "이미지" : "동영상"}는 ${
            type === "image" ? "25MB" : "100MB"
          } 이하만 업로드할 수 있습니다.`
        );
        continue;
      }

      const preview = URL.createObjectURL(file);
      const mediaFile: MediaFile = { file, type, preview };

      // GPS 정보 추출 시도
      try {
        const gpsInfo = await extractGPSFromMediaFile(file);
        mediaFile.gpsInfo = gpsInfo;

        if (gpsInfo) {
          console.log(`${file.name}에서 GPS 정보 추출됨:`, gpsInfo);
        }
      } catch (error) {
        console.error(`${file.name}에서 GPS 정보 추출 실패:`, error);
        mediaFile.gpsInfo = null;
      }

      // 이미지인 경우 최적화 (선택적)
      if (type === "image") {
        try {
          // 고품질 원본 유지 옵션: 3MB 이하 원본은 그대로 사용
          if (file.size <= 3 * 1024 * 1024) {
            console.log("원본 품질 유지:", file.name);
            // 원본 파일을 그대로 사용 (최고 품질)
            mediaFile.optimized = file;
          } else {
            // 3MB 초과시에만 최적화 적용
            const optimizedFile = await optimizeImage(file);
            mediaFile.optimized = optimizedFile;
            console.log("이미지 최적화 적용:", file.name);
          }
        } catch (error) {
          console.error("이미지 최적화 실패:", error);
          // 최적화 실패시 원본 사용
          mediaFile.optimized = file;
        }
      }

      setMediaFiles((prev) => [...prev, mediaFile]);
    }
  };

  // 이미지 최적화 (고품질)
  const optimizeImage = (file: File): Promise<File> => {
    return new Promise((resolve) => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d")!;
      const img = new Image();

      img.onload = () => {
        // 더 높은 최대 크기 설정
        const maxWidth = 1920;
        const maxHeight = 1280;

        let { width, height } = img;

        // 원본 크기가 최대 크기보다 작으면 리사이징 하지 않음
        const needsResize = width > maxWidth || height > maxHeight;

        if (needsResize) {
          if (width > height) {
            if (width > maxWidth) {
              height = (height * maxWidth) / width;
              width = maxWidth;
            }
          } else {
            if (height > maxHeight) {
              width = (width * maxHeight) / height;
              height = maxHeight;
            }
          }
        }

        canvas.width = width;
        canvas.height = height;

        // 고품질 렌더링 설정
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = "high";

        ctx.drawImage(img, 0, 0, width, height);

        // 원본 파일 형식 유지 (PNG는 PNG로, JPEG는 JPEG로)
        const outputFormat =
          file.type === "image/png" ? "image/png" : "image/jpeg";
        const quality = outputFormat === "image/jpeg" ? 0.9 : 0.95; // JPEG: 90% 품질, PNG: 95% 품질

        console.log("최적화 시작", quality);

        canvas.toBlob(
          (blob) => {
            if (blob) {
              const optimizedFile = new File([blob], file.name, {
                type: outputFormat,
                lastModified: Date.now(),
              });
              resolve(optimizedFile);
            } else {
              resolve(file);
            }
          },
          outputFormat,
          quality
        );
      };

      img.src = URL.createObjectURL(file);
    });
  };

  // 미디어 파일 삭제
  const removeMediaFile = (index: number) => {
    setMediaFiles((prev) => {
      const newFiles = [...prev];
      URL.revokeObjectURL(newFiles[index].preview);
      newFiles.splice(index, 1);
      return newFiles;
    });
  };

  // 해시태그 추가
  const addHashtag = () => {
    const tag = hashtagInput.trim().replace("#", "");
    if (tag && !hashtags.includes(tag)) {
      setHashtags((prev) => [...prev, tag]);
      setHashtagInput("");
    }
  };

  // 해시태그 삭제
  const removeHashtag = (index: number) => {
    setHashtags((prev) => prev.filter((_, i) => i !== index));
  };

  // 피드 작성 (개선된 에러 처리)
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser) return;

    // CORS 디버깅 정보 출력
    console.log("업로드 시작 - 환경 정보:", {
      origin: window.location.origin,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
      userId: currentUser.uid,
      mediaFilesCount: mediaFiles.length,
    });

    setIsLoading(true);
    setError("");

    try {
      // 미디어 파일 사전 검증
      if (mediaFiles.length > 0) {
        for (const media of mediaFiles) {
          const fileToUpload = media.optimized || media.file;

          if (!fileToUpload || fileToUpload.size === 0) {
            alert("유효하지 않은 파일이 있습니다. 다시 선택해주세요.");
            return;
          }

          if (fileToUpload.size > 25 * 1024 * 1024) {
            alert(`파일 "${fileToUpload.name}"이 25MB를 초과합니다.`);
            return;
          }
        }
      }

      // Firebase Storage 연결 테스트
      const storageConnected = await testStorageConnection();
      if (!storageConnected) {
        throw new Error(
          "Storage 연결에 실패했습니다. 네트워크를 확인해주세요."
        );
      }

      // 인증 토큰 갱신
      await currentUser.getIdToken(true);
      console.log("인증 토큰 갱신 완료");

      // 첫 번째 미디어 파일의 GPS 정보 사용
      const firstMediaGPS =
        mediaFiles.length > 0 ? mediaFiles[0].gpsInfo : null;

      // 임시 피드 ID 생성 (파일 업로드용)
      const tempFeedId = `temp_${Date.now()}_${Math.random()
        .toString(36)
        .substring(2)}`;

      // 미디어 파일이 있으면 먼저 업로드
      let uploadedMediaFiles: any[] = [];
      if (mediaFiles.length > 0) {
        console.log("미디어 파일 업로드 시작...");

        try {
          uploadedMediaFiles = await uploadMultipleMediaFiles(
            mediaFiles.map((media) => ({
              file: media.file,
              optimized: media.optimized,
              type: media.type,
            })),
            currentUser.uid,
            tempFeedId
          );
          console.log("미디어 파일 업로드 완료:", uploadedMediaFiles);
        } catch (uploadError) {
          console.error("미디어 파일 업로드 실패:", uploadError);
          throw new Error("이미지 업로드에 실패했습니다. 다시 시도해주세요.");
        }
      }

      const feedData = {
        content: content.trim(),
        hashtags,
        locationInfo: firstMediaGPS || undefined,
        mediaFiles: uploadedMediaFiles,
      };

      await createFeed(feedData);

      alert("피드가 성공적으로 작성되었습니다!");
      navigate("/");
    } catch (error) {
      console.error("피드 작성 오류:", error);

      let errorMessage = "피드 작성 중 오류가 발생했습니다.";

      if (error instanceof Error) {
        if (error.message.includes("Storage")) {
          errorMessage =
            "이미지 업로드에 실패했습니다. 네트워크 연결을 확인하고 다시 시도해주세요.";
        } else if (error.message.includes("권한")) {
          errorMessage = "업로드 권한이 없습니다. 로그인 상태를 확인해주세요.";
        } else if (error.message.includes("크기")) {
          errorMessage =
            "파일 크기가 너무 큽니다. 25MB 이하의 파일을 사용해주세요.";
        } else {
          errorMessage = error.message;
        }
      }

      alert(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // GPS 정보가 있는 미디어 개수 계산
  const mediaWithGPS = mediaFiles.filter((media) => media.gpsInfo).length;

  return (
    <div className={styles.createFeedPage}>
      {/* 헤더 */}
      <div className={styles.header}>
        <button className={styles.backButton} onClick={handleBack}>
          <img src="/images/icons/arrow-left.svg" alt="뒤로가기" />
        </button>
        <h1>피드 작성</h1>
        <button
          className={styles.submitButton}
          onClick={handleSubmit}
          disabled={isLoading || !content.trim()}
        >
          {isLoading ? "작성중..." : "완료"}
        </button>
      </div>

      <div className={styles.container}>
        {/* 피드 내용 */}
        <div className={styles.contentSection}>
          <textarea
            className={styles.contentInput}
            placeholder="무슨 일이 일어나고 있나요?"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            maxLength={1000}
          />
          <div className={styles.characterCount}>{content.length}/1000</div>
        </div>

        {/* 미디어 파일 */}
        <div className={styles.mediaSection}>
          <div className={styles.mediaHeader}>
            <h3>사진/동영상</h3>
            <button
              className={styles.addMediaButton}
              onClick={() => fileInputRef.current?.click()}
              disabled={mediaFiles.length >= 10}
            >
              <img src="/images/icons/camera.svg" alt="사진 추가" />
              추가 ({mediaFiles.length}/10)
            </button>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,video/*"
            multiple
            onChange={handleFileSelect}
            style={{ display: "none" }}
          />

          {mediaFiles.length > 0 && (
            <>
              <div className={styles.mediaGrid}>
                {mediaFiles.map((media, index) => (
                  <div key={index} className={styles.mediaItem}>
                    {media.type === "image" ? (
                      <img src={media.preview} alt={`미디어 ${index + 1}`} />
                    ) : (
                      <video
                        src={media.preview}
                        controls
                        preload="metadata"
                        muted
                      />
                    )}
                    <button
                      className={styles.removeMediaButton}
                      onClick={() => removeMediaFile(index)}
                    >
                      ×
                    </button>
                    {media.gpsInfo && (
                      <div className={styles.locationBadge}>📍 위치 정보</div>
                    )}
                  </div>
                ))}
              </div>

              {/* GPS 정보 요약 */}
              {mediaWithGPS > 0 && (
                <div className={styles.gpsInfo}>
                  <img src="/images/icons/location.svg" alt="위치" />
                  <span>
                    {mediaWithGPS}개 파일에서 위치 정보가 발견되었습니다
                  </span>
                </div>
              )}
            </>
          )}
        </div>

        {/* 해시태그 */}
        <div className={styles.hashtagSection}>
          <h3>해시태그</h3>
          <div className={styles.hashtagInput}>
            <input
              type="text"
              placeholder="해시태그 입력 (예: 강아지)"
              value={hashtagInput}
              onChange={(e) => setHashtagInput(e.target.value)}
              onKeyPress={(e) => e.key === "Enter" && addHashtag()}
            />
            <button onClick={addHashtag}>추가</button>
          </div>

          {hashtags.length > 0 && (
            <div className={styles.hashtagList}>
              {hashtags.map((tag, index) => (
                <span key={index} className={styles.hashtagItem}>
                  #{tag}
                  <button onClick={() => removeHashtag(index)}>×</button>
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateFeedPage;
